/**
 * CR220: Public Deals Hook
 * 
 * Fetches deals for the public site from the public-deals-available endpoint.
 * Uses the central toUiDeal mapper for consistent shaping with admin previews.
 */

import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Deal } from "@/lib/types/deals";
import { toUiDeal, type RawDealInput, type UIDeal } from "@/lib/deals/toUiDeal";
import { hydrateFlyerArtifactsBatch } from "@/lib/deals/fetchFlyerSnippetForDeal";

export interface UsePublicDealsResult {
  deals: Deal[];
  asOf: string | null;
  totalCount: number;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch public deals from the public-deals-available endpoint.
 * This fetches production deals only from the deals table.
 */
export function usePublicDeals(): UsePublicDealsResult {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [asOf, setAsOf] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke("public-deals-available");

      if (fnError) {
        console.error("[usePublicDeals] Function error:", fnError);
        setError(fnError.message);
        return;
      }

      if (data.error) {
        console.error("[usePublicDeals] API error:", data.error);
        setError(data.error);
      }

      // CR220: Use central toUiDeal mapper for consistent shaping
      const rawDeals = (data.deals || []) as RawDealInput[];
      const mappedDealsRaw: Deal[] = rawDeals.map(d => toUiDeal(d, "live"));
      // CR447: Batch hydrate flyer snippets (no N+1)
      const mappedDeals = await hydrateFlyerArtifactsBatch(mappedDealsRaw) as Deal[];
      
      // CR230: Use total_count from API (not deals.length which may be paginated)
      const apiTotalCount = data.total_count ?? mappedDeals.length;
      const returnedCount = data.returned_count ?? mappedDeals.length;
      
      // CR230: Enhanced diagnostics for debugging blank screen issues
      const diagnostics = {
        rawDealsFromApi: rawDeals.length,
        returnedCount,
        totalCount: apiTotalCount,
        activeSnapshot: data.active_snapshot || null,
        policyMode: data.policy?.mode || "normal",
        pagination: data.pagination || null,
        // Date source tier distribution
        dateTiers: mappedDeals.reduce((acc, d) => {
          const tier = (d as UIDeal)._dateSourceTier || "unknown";
          acc[tier] = (acc[tier] || 0) + 1;
          return acc;
        }, {} as Record<string, number>),
        // Confidence distribution
        confidenceCounts: mappedDeals.reduce((acc, d) => {
          acc[d.confidence || "unknown"] = (acc[d.confidence || "unknown"] || 0) + 1;
          return acc;
        }, {} as Record<string, number>),
      };
      
      console.log(`[CR230][usePublicDeals] Fetch complete: returned=${returnedCount} total=${apiTotalCount}`, diagnostics);
      
      // CR230: Warn if 0 deals returned (helps debug blank screens)
      if (mappedDeals.length === 0) {
        console.warn(`[CR230][usePublicDeals] WARNING: 0 deals returned. Check snapshot=${data.active_snapshot}, policy=${data.policy?.mode}`);
      }
      
      setDeals(mappedDeals);
      setAsOf(data.as_of || null);
      setTotalCount(apiTotalCount);
    } catch (err) {
      console.error("[usePublicDeals] Unexpected error:", err);
      setError(String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    deals,
    asOf,
    totalCount,
    loading,
    error,
    refetch,
  };
}
